import { find, first, get, last } from "lodash"
import { createSelector } from "reselect"
import { percentageFormatter, makeCurrencyFormatter } from "helpers/formatters/numbers"
import { formatLongDate } from "helpers/formatters/dates"
import { getMostUsedOrDefaultModelId } from "helpers/models"
import { UPLOAD_CONFIG } from "components/upload/constants"
import { getModels } from "resources/models"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

const getPortfolios = state => get(state, "global.data.portfolios.data", EMPTY_ARRAY)
const getRawScanResult = (state) => get(state, "modules.swapPositions.scanResult")
const getAccountMeta = state => get(state, "global.selected.accountMeta")

export const getSwap = state => get(state, "global.data.swap.data.swap")
export const getFiles = (state) => get(state, "modules.swapPositions.files")
export const getUploadedFiles = (state) => get(state, "modules.swapPositions.uploadedFiles")
export const getUploadOption = (state) => get(state, "modules.swapPositions.uploadOption")
export const getUploadDateTime = (state) => get(state, "modules.swapPositions.uploadDateTime")
export const getSelectedModel = (state) => get(state, "modules.swapPositions.modelId")
export const getAccount = state => get(state, "global.selected.account")
export const getUploadConfig = () => ({ ...UPLOAD_CONFIG })

export const getModel = createSelector(
  [getSelectedModel, getModels, getPortfolios, getSwap],
  (selectedModel, models, portfolios, swap) => {
    if (swap) {
      if (selectedModel) return selectedModel

      return getMostUsedOrDefaultModelId(portfolios, models)
    }

    return null
  },
)

export const getSwapDetails = createSelector(
  [getSwap, getModel, getModels],
  (swap, modelId, models) => {
    if (swap) {
      return ({
        id: swap.ticker,
        name: swap.ticker,
        availableFrom: swap.availableFrom,
        terminationDate: swap.terminationDate,
        modelId,
        model: get(find(models, { id: modelId }), "name"),
      })
    }

    return null
  },
)

const getCurrency = createSelector(
  [getAccountMeta, getSwap],
  (accountMeta, swap) => get(swap, "currencyOverride") || get(accountMeta, "defaultCurrency"),
)

export const getScanResult = createSelector(
  [getRawScanResult, getCurrency],
  (scanResult, currency) => {
    if (scanResult) {
      const scanSummary = get(scanResult, "scan.summary", EMPTY_OBJECT)
      const scanDates = get(scanResult, "scan.dates", EMPTY_ARRAY)

      return {
        ...scanResult,
        summary: {
          items: {
            averageEquity: {
              label: "Average Equity",
              value: makeCurrencyFormatter(currency)(scanSummary.averageEquity || scanSummary.averageGmv),
            },
            modelCoverage: {
              label: "Model Coverage",
              value: percentageFormatter(scanSummary.averageCoverage / 100),
            },
            numberOfDates: {
              label: "Dates found",
              value: scanDates.length,
            },
            averageSecurities: {
              label: "Average Number of Securities",
              value: scanSummary.averageSecurities,
            },
          },
          from: formatLongDate(get(first(scanDates), "date")),
          to: formatLongDate(get(last(scanDates), "date")),
        },
      }
    }

    return null
  },
)
